// +----------------------------------------------------------------------
// | 想想会员 /vip
// |
// | 为什么从「我的」页独立出来：
// |   原来会员卡整块挂在「我的」页顶部 —— 已开通时是渐变卡 + 续费按钮，
// |   未开通时是价目签 + 兑换表单 + 扫码按钮，把资料页挤得很满；
// |   而资料页的主体是「改资料」，两者抢注意力。
// |   拆开后：我的页只留一行「想想会员 · 不是会员 / 有效期至 X」，
// |   点进来才是完整的开通/续费流程。
// |
// | 做成沉浸子页（手机端藏顶栏/底 Tab，页面自己带「←」）：
// |   和 /free-code 同理 —— 从「我的」点进来的二级页，不承担品牌门面，
// |   藏掉顶栏更聚焦「开通」这一件事。布局走 .layout--imm（普通滚动）。
// |
// | 三个区块的顺序就是用户的决策顺序：
// |   ① 状态（我现在是什么） → ② 权益（值不值） → ③ 怎么买（价目/扫码/兑换码）
// +----------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAlipayOrder, getMembership, redeemCode } from '../../api/order';
import { toast } from '../../components/Toast';
import './Vip.less';

/** 「2026-10-20 18:29:40」→「2026.10.20」 */
function prettyDate(value) {
  if (!value) return '';
  return String(value).slice(0, 10).replace(/-/g, '.');
}

/** 1900（分）→「¥19」，500（分）→「¥5」：去掉小数尾部的 0 */
function yuan(amount) {
  return `¥${(amount / 100).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;
}

/** 折合月单价，让「买长的更划算」一眼可见 */
function perMonth(amount, days) {
  if (!days) return '';
  return yuan(Math.round((amount / days) * 30));
}

/**
 * 价目签第三行：天卡按天、短档按周、长档按月折算。
 * 天卡（days===1）必须单独一刀 —— 走「按周折算」会算出 (100/1)*7 = ¥7/周，
 * 既突兀又不成立（没人会连买 7 张天卡），单位标签也会跟着错成「/周」。
 * 周卡（days<=14）若硬折月会得出 ¥29/月 的天价，反而把周卡丑化了，
 * 所以短档显示「X/周」，长档维持「X/月」。
 */
function unitPrice(p) {
  if (!p || !p.days) return '';
  if (p.days === 1) return yuan(p.amount);
  if (p.days <= 14) return yuan(Math.round((p.amount / p.days) * 7));
  return perMonth(p.amount, p.days);
}

/** 价目签第三行的单位后缀，跟 unitPrice 的分档必须保持一致 */
function priceUnit(p) {
  if (!p || !p.days) return '月';
  if (p.days === 1) return '天';
  return p.days <= 14 ? '周' : '月';
}

// iOS 判断：壳里优先 plus.os，浏览器看 UA（iPadOS 13+ 伪装 Mac 用触点兜底）。
// 模块顶层算一次就行 —— 用户不会中途换设备。
const isIOSDevice = (() => {
  try {
    if (typeof window !== 'undefined' && window.plus && window.plus.os && window.plus.os.name === 'iOS') {
      return true;
    }
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
  } catch (e) { /* ignore */ }
  return false;
})();

// https 收银台 → alipays:// 深链（安卓直起支付宝 App 的标准做法，官方文档口径）。
// 只转支付宝自家域名，别的外链不碰。沙箱网关 openapi-sandbox.dl.alipaydev.com
// 不是 alipay.com 域 → 不转，走浏览器开 H5 收银台（沙箱买家账号网页可付）。
const ALIPAY_SCHEME_PREFIX = 'alipays://platformapi/startapp?appId=20000067&url=';
function toAlipayScheme(httpUrl) {
  if (/^alipay/i.test(httpUrl)) return httpUrl;
  if (!/(^|\.)alipay\.com\//i.test(httpUrl)) return '';
  return ALIPAY_SCHEME_PREFIX + encodeURIComponent(httpUrl);
}

export default function Vip() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);
  const [quota, setQuota] = useState(null);
  // 「帮我想想」的额度（与聊天 quota 是两本账，接口分字段给）
  const [decision_quota, setDecisionQuota] = useState(null);
  const [products, setProducts] = useState([]);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showQr, setShowQr] = useState(false);   // 扫码兜底弹层（入口已下线，保留备用）

  // 支付宝支付：默认选第一档（月付），点价目签切换，点大按钮跳支付宝
  const [picked, setPicked] = useState('vip_month');
  const [paying, setPaying] = useState(false);

  // 支付失败的原因要「留在页面上」，不能只用 toast ——
  // toast 两秒就没了，用户看到的就是「点了没反应」（真实反馈）。
  const [payErr, setPayErr] = useState('');
  const [showCopy, setShowCopy] = useState(false);   // 复制弹层（跳不过去时的兜底）
  const [copying, setCopying] = useState(false);
  const [lastUrl, setLastUrl] = useState('');        // 最近一次下单的收银台链接
  const jumpTimer = useRef(null);
  // 微信内置浏览器打不开支付宝（腾讯自家互相屏蔽），提前说清楚比让他白点强
  const inWechat = typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent);

  // 支付宝付完会把用户带回 /vip?paid=1。回调可能有几秒延迟（先于我们的
  // 异步通知到达），所以只提示一句，状态以页面正常拉取的结果为准。
  useEffect(() => {
    if (window.location.search.includes('paid=1')) {
      toast('已回到想想。会员生效有几秒延迟，没变的话稍后刷新看看', 'success');
      window.history.replaceState({}, '', '/vip');
    }
  }, []);

  // 返回逻辑已上收到 Layout 的子页返回钮（location.key==='default' 时回「我的」）

  const load = useCallback(async () => {
    try {
      const data = await getMembership();
      setMembership(data.membership || null);
      setQuota(data.quota || null);
      setDecisionQuota(data.decision_quota || null);
      setProducts(data.products || []);
    } catch (e) {
      // 拉不到就按免费态渲染，不弹错 —— 这页的其它内容（价目、兑换）还能用
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRedeem = async (e) => {
    e.preventDefault();
    const value = code.trim();
    if (!value) {
      toast('请输入兑换码', 'error');
      return;
    }
    if (submitting) return;

    setSubmitting(true);
    try {
      const data = await redeemCode(value);
      setMembership(data.membership || null);
      if (data.quota) setQuota(data.quota);
      if (data.decision_quota) setDecisionQuota(data.decision_quota);
      setCode('');
      toast(data.extended ? '兑换成功，有效期已叠加' : '兑换成功，会员已开通', 'success');
    } catch (err) {
      // 已被用过的卡重输没有意义，单独提示一句，省得用户反复试
      const msg = err.code === 4101
        ? '这张卡已经被用过了，找发卡人核对一下'
        : err.message || '兑换没成功，检查一下卡号';
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const isMember = !!(membership && membership.is_member);
  const daysLeft = membership ? membership.days_left : 0;
  const freeLimit = quota && quota.unlimited === false ? quota.limit : null;

  // 「帮我想想」的权益说明。
  // 和聊天额度是两本账：membership 接口分开给 quota（聊天）和 decision_quota
  //（帮我想想）—— 这里取后者。freeLimit 兜底 3 与 config/quota.php 的
  // decision_daily 默认值一致，接口挂了也不会写出错误数字。
  const decisionFree = decision_quota && decision_quota.unlimited === false
    ? decision_quota.limit
    : 3;
  const decisionLine = isMember
    ? '你现在就能无限次问，不用数着用'
    : `免费版每天 ${decisionFree} 次，会员无限次问`;
  // 免费态状态卡的第二行：聊天条数 + 帮我想想次数一起说
  const freeLine = [
    freeLimit ? `每天 ${freeLimit} 条对话` : '对话按天限量',
    `帮我想想 ${decisionFree} 次`,
  ].join(' · ');
  const freeLeft = quota && quota.unlimited === false && quota.remaining !== undefined
    ? `今天还剩 ${quota.remaining} 条对话`
    : '';

  // 支付跳转。三种环境一套代码，差别只在「怎么把 URL 交出去」：
  //   ① 原生壳（APK/IPA）：必须走 __xxShell.openUrl() 交给系统。
  //      直接 location.href 会被 WebView 吞掉（壳会拦站外跳转，
  //      而 alipays:// 这种自定义 scheme 更是必须由宿主调 startActivity）——
  //      表现就是「点了没反应」，这就是 APK 里支付不好使的真凶。
  //   ② 微信内置浏览器：腾讯互相屏蔽支付宝，直接告诉用户去浏览器打开。
  //   ③ 普通浏览器：location.href 就是标准做法，别动。
  //
  // 为什么错误要常驻在页面上：用户反馈「点支付没反应」——实际是后端回了
  // 一句「还没配置好」，toast 两秒飘走，人根本没看见。
  const handleAlipay = async () => {
    if (paying) return;

    const target = products.find((p) => p.key === picked) || products[0];
    if (!target) {
      setPayErr('暂时没有可买的档位，稍后再试。');
      return;
    }

    if (inWechat) {
      setPayErr('微信里打不开支付宝。点右上角「⋯」选「在浏览器打开」，再回来付款。');
      return;
    }

    setPayErr('');
    setPaying(true);

    try {
      const data = await createAlipayOrder(target.key);
      const url = data && data.pay_url;
      if (!url) throw new Error('没拿到支付链接，稍后再试');

      setLastUrl(url);

      // 壳里（APK/IPA）：交给系统层打开 —— 探测 window.plus 本体。
      // **故意不依赖 __xxShell.openUrl**：shell.js 是无 hash 的运行时文件，
      // nginx 没配 no-cache 时手机上会长期吃旧缓存，和带 hash 的新 bundle
      // 版本错位（真机踩过：a.openUrl is not a function）。支付是关键路径，
      // 必须自包含，shell.js 新旧都不影响这逻辑。
      try {
        const p = window.plus;
        if (p && p.runtime && typeof p.runtime.openURL === 'function') {
          // 安卓转 alipays:// 深链直起支付宝 App；iOS 不转（自定义 scheme
          // 付完回不来本 App），保持 https 走 Safari。
          const target = isIOSDevice ? url : toAlipayScheme(url) || url;
          p.runtime.openURL(target, () => {
            // 交给系统但没开成（极少数机型被拦）：亮复制兜底
            setPayErr('没能打开支付宝，把链接复制出来用浏览器打开就能付。');
            setShowCopy(true);
          });
          setPaying(false);
          return;
        }
      } catch (e) {
        // openURL 抛异常 → 落到下面的标准跳转，别把支付卡死在这
      }

      window.location.href = url;
      if (jumpTimer.current) clearTimeout(jumpTimer.current);
      jumpTimer.current = setTimeout(() => setPaying(false), 2500);
    } catch (err) {
      if (jumpTimer.current) clearTimeout(jumpTimer.current);
      setPaying(false);
      setPayErr(err.message || '支付没成功，稍后再试');
    }
  };

  // 复制最近一次下单的收银台链接。
  // 为什么需要：壳里跳不出去时（或用户在微信里被拦时），把链接拿到系统浏览器
  // 打开是唯一出路。两层降级 —— clipboard API 失败再走 textarea + execCommand
  //（老 WebView 上 clipboard API 常常不可用）。
  const doCopy = async (text) => {
    if (!text) return false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('copy failed');
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const copyLastUrl = async () => {
    if (copying) return;
    setCopying(true);
    const ok = await doCopy(lastUrl);
    setCopying(false);
    if (ok) {
      toast('链接已复制，粘到浏览器打开就能付款', 'success');
      setShowCopy(false);
    } else {
      toast('复制没成功，长按下面的链接手动复制', 'error');
    }
  };

  useEffect(() => () => {
    if (jumpTimer.current) clearTimeout(jumpTimer.current);
  }, []);

  return (
    <div className="page vip">
      {/* 顶栏（返回钮 + 居中标题「想想会员」）由 Layout 统一提供（2026-09-29
          子页顶栏大统一），页面里不再自绘 vip-topbar */}

      {/* ① 状态 */}
      {loading ? (
        <div className="card vip-status vip-status--loading">
          <span className="muted">正在看你的会员状态…</span>
        </div>
      ) : isMember ? (
        <div className="card vip-status is-member">
          <div className="vip-status-head">
            <strong>想想会员</strong>
            <span className="vip-status-badge">不限次</span>
          </div>
          <p className="vip-status-date">有效期至 {prettyDate(membership.expire_at)}</p>
          <p className="vip-status-sub">帮我想想 + 聊天都不限次 · 还剩 {daysLeft} 天</p>
          <p className="vip-status-sub">到期后回到免费版，不会自动扣费</p>
        </div>
      ) : (
        <div className="card vip-status is-free">
          <div className="vip-status-head">
            <strong>还不是会员</strong>
          </div>
          <p className="vip-status-date">现在用的是免费版</p>
          <p className="vip-status-sub">
            {freeLine}
            {freeLeft ? ` · ${freeLeft}` : ''}
          </p>
        </div>
      )}

      {/* ② 权益：只写真的，别为了卖相编 */}
      <div className="card vip-benefits">
        <h3 className="vip-h3">会员不一样在哪</h3>
        <ul>
          {/* 「帮我想想」放第一条：首页那个「会员不限次 ›」的链接就指着它，
              点进来必须第一眼看到 —— 否则用户会觉得被首页骗了。 */}
          <li>
            <span className="vip-benefit-tick">✓</span>
            <span>
              <strong>帮我想想，不限次</strong>
              <i>{decisionLine}</i>
            </span>
          </li>
          <li>
            <span className="vip-benefit-tick">✓</span>
            <span>
              <strong>和想想聊，不限次</strong>
              <i>免费版每天 {freeLimit || 10} 条，会员随便聊</i>
            </span>
          </li>
          <li>
            <span className="vip-benefit-tick">✓</span>
            <span>
              <strong>按天算，不用不亏心</strong>
              <i>到期自动回到免费版，不会偷偷扣费</i>
            </span>
          </li>
        </ul>
      </div>

      {/* ③ 怎么买：支付宝直接付，扫码转账留作兜底 */}
      <div className="card vip-buy">
        <h3 className="vip-h3">{isMember ? '续费（有效期往后叠加）' : '开通会员'}</h3>

        <div className="vip-prices">
          {products.map((p) => (
            <button
              type="button"
              key={p.key}
              className={`vip-price${picked === p.key ? ' is-picked' : ''}`}
              onClick={() => setPicked(p.key)}
              aria-pressed={picked === p.key}
            >
              <i>{p.name.replace('想想会员 · ', '')}</i>
              <strong>{yuan(p.amount)}</strong>
              <span>{unitPrice(p)}/{priceUnit(p)}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-primary btn-block vip-buy-btn"
          disabled={paying || products.length === 0}
          onClick={handleAlipay}
        >
          {paying
            ? '正在跳转支付宝…'
            : (() => {
                // yuan() 已经带 ¥ 了，别再拼一个（曾拼出「支付宝支付 ¥¥19」）
                const t = products.find((p) => p.key === picked);
                return t ? `支付宝支付 ${yuan(t.amount)}` : '支付宝支付';
              })()}
        </button>

        {/* 支付失败常驻提示：说明原因，不自动消失（toast 会飘走，用户就说「没反应」） */}
        {payErr && (
          <div className="vip-pay-err" role="alert">
            <p>{payErr}</p>
          </div>
        )}

        {/* 跳不过去时的兜底入口 —— 暂时下线（2026-09-24），想恢复取消注释即可。
        {lastUrl && (
          <button type="button" className="vip-pay-rescue" onClick={() => setShowCopy(true)}>
            跳到支付宝没反应？点这里
          </button>
        )}
        */}

        <p className="vip-note muted">
          跳到支付宝付款，付完自动回来，会员立即生效。
          {isMember ? ' 续费会在现有有效期上叠加。' : ''}
        </p>

        {/* 微信扫码转账（备用）—— 已接入支付宝在线支付，暂时下线。
            想恢复时取消下面这段注释即可（弹层 JSX 在文件末尾也还留着）。
        <button type="button" className="btn btn-ghost btn-block vip-qr-btn" onClick={() => setShowQr(true)}>
          微信扫码转账（备用）
        </button>
        */}
      </div>

      <div className="card vip-redeem">
        <h3 className="vip-h3">有兑换码？</h3>
        <form className="vip-redeem-form" onSubmit={handleRedeem}>
          <input
            className="input"
            type="text"
            value={code}
            maxLength={32}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="输入兑换码，如 ABCD-EFGH-IJKL"
          />
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? '兑换中…' : '兑换'}
          </button>
        </form>
        <p className="vip-note muted">
          不用区分大小写，中间的短横线加不加都行
          {isMember ? '；已有会员会往后叠加' : ''}。
        </p>
        <Link to="/free-code" className="vip-link">
          还没有码？去领一张 7 天体验码 ›
        </Link>
      </div>

      {/* 复制链接弹层：壳里跳不出去 / 微信拦了支付宝时的兜底。
          链接是可选中的纯文本，就算「复制」按钮失败也能长按手动复制。 */}
      {showCopy && (
        <div className="vip-modal" onClick={() => setShowCopy(false)}>
          <div className="vip-modal-box" onClick={(e) => e.stopPropagation()}>
            <strong>跳到支付宝没反应？</strong>
            <p className="vip-copy-tip">
              把下面这串链接复制出来，用手机浏览器打开就能付款。付完你的会员一样会生效。
            </p>
            <textarea
              className="vip-copy-url"
              readOnly
              rows={3}
              value={lastUrl}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={copying}
              onClick={copyLastUrl}
            >
              {copying ? '正在复制…' : '复制链接'}
            </button>
            <button type="button" className="vip-modal-cancel" onClick={() => setShowCopy(false)}>
              关掉
            </button>
          </div>
        </div>
      )}

      {/* 扫码购买弹层：点遮罩关 —— 入口已随「微信扫码转账」按钮一起下线，
          暂时不会触发。保留代码是为了哪天想恢复扫码兜底时直接用。 */}
      {showQr && (
        <div className="vip-modal" onClick={() => setShowQr(false)}>
          <div className="vip-modal-box" onClick={(e) => e.stopPropagation()}>
            <strong>微信扫码，找「salidy」开通</strong>
            <img src="/wechat-qrcode.jpg" alt="微信名片二维码" />
            <p>加上微信说一声要哪档，转账后兑换码马上发你，回到这里输入即可。</p>
            <button type="button" className="btn btn-primary" onClick={() => setShowQr(false)}>
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
